import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet } from '@tanstack/react-router';
import { DeleteAccountScreen } from './legal/DeleteAccountScreen.tsx';
import { LegalScreen } from './legal/LegalScreen.tsx';
import { AccountScreen } from './screens/AccountScreen.tsx';
import { BrandingScreen } from './screens/BrandingScreen.tsx';
import { HomeScreen } from './screens/HomeScreen.tsx';
import { PlansScreen } from './screens/PlansScreen.tsx';
import { ProjectScreen } from './screens/ProjectScreen.tsx';
import { SettingsScreen } from './screens/SettingsScreen.tsx';
import { SignInScreen } from './screens/SignInScreen.tsx';
import { InviteScreen } from './team/InviteScreen.tsx';
import { TeamNewScreen } from './team/TeamNewScreen.tsx';
import { TeamScreen } from './team/TeamScreen.tsx';
import { FeedbackHost } from './ui/FeedbackHost.tsx';
import { Paywall } from './ui/Paywall.tsx';
import { UpdateBanner } from './ui/UpdateBanner.tsx';

const rootRoute = createRootRoute({
  component: () => (
    <>
      <UpdateBanner />
      <Outlet />
      <FeedbackHost />
      <Paywall />
    </>
  ),
});

const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: HomeScreen });

export type ProjectTab = 'overview' | 'files' | 'quote';
export type FilesFilter = 'all' | 'ai' | 'exports';

export const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId',
  validateSearch: (s: Record<string, unknown>): { tab?: ProjectTab; filter?: FilesFilter; rename?: boolean } => ({
    tab: s.tab === 'files' || s.tab === 'quote' ? s.tab : undefined,
    filter: s.filter === 'ai' || s.filter === 'exports' ? s.filter : undefined,
    rename: s.rename === true || s.rename === 1 || s.rename === '1' ? true : undefined,
  }),
  component: ProjectScreen,
});

export const editorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/edit',
  component: lazyRouteComponent(() => import('./screens/editor/EditorScreen.tsx'), 'EditorScreen'),
});

const settingsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings', component: SettingsScreen });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin',
  validateSearch: (s: Record<string, unknown>): { next?: string } => ({
    next: typeof s.next === 'string' && s.next.startsWith('/') ? s.next : undefined,
  }),
  component: SignInScreen,
});
const accountRoute = createRoute({ getParentRoute: () => rootRoute, path: '/account', component: AccountScreen });
const plansRoute = createRoute({ getParentRoute: () => rootRoute, path: '/plans', component: PlansScreen });

const brandingRoute = createRoute({ getParentRoute: () => rootRoute, path: '/branding', component: BrandingScreen });
const teamNewRoute = createRoute({ getParentRoute: () => rootRoute, path: '/team/new', component: TeamNewScreen });
const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/team/$orgId',
  validateSearch: (s: Record<string, unknown>): { checkout?: string; tab?: string } => ({
    checkout: typeof s.checkout === 'string' ? s.checkout : undefined,
    tab: typeof s.tab === 'string' ? s.tab : undefined,
  }),
  component: TeamScreen,
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite',
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  component: InviteScreen,
});

// Public pages (no sign-in): store listings and Play's data-deletion form link here.
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: () => <LegalScreen doc="privacy" />,
});
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: () => <LegalScreen doc="terms" />,
});
const deleteAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/delete-account',
  component: DeleteAccountScreen,
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  projectRoute,
  editorRoute,
  settingsRoute,
  signInRoute,
  accountRoute,
  plansRoute,
  brandingRoute,
  teamNewRoute,
  teamRoute,
  inviteRoute,
  privacyRoute,
  termsRoute,
  deleteAccountRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
